import React, { useEffect, useMemo, useState } from 'react';
import { Button, Cascader } from 'antd';
import { CascaderOptionType } from 'antd/lib/cascader';
import { useFetchWebsiteIndexEntriesQuery } from '../../deepAnalysis/websiteIndexEntry/service';
import { isUrlValid } from '../utils';
import { useLazyFetchDomainUrlsQuery } from '../../analysis/service';

function stripDomainWebsitePrefix(domain: string) {
    if (domain.startsWith('www.')) {
        return domain.substring(4);
    }
    return domain;
}

type Props = {
    reportUrl: string | undefined;
    onReportUrlChange: (newUrl: string) => void;
};

export default function SavedReportsSelector({ reportUrl, onReportUrlChange }: Props) {
    const [domainCascaderOptions, setDomainCascaderOptions] = useState<CascaderOptionType[]>([]);

    const { data: websiteIndexEntries } = useFetchWebsiteIndexEntriesQuery();
    const domains = useMemo(
        () => websiteIndexEntries?.map((website) => website.domain).filter((domain, index, array) => array.indexOf(domain) === index),
        [websiteIndexEntries]
    );
    const strippedReportDomain = reportUrl && isUrlValid(reportUrl) ? stripDomainWebsitePrefix(new URL(reportUrl).hostname) : null;

    const [fetchDomainUrls] = useLazyFetchDomainUrlsQuery();

    const getOptionChildren = async (domain: string, nextToken?: string) => {
        let children: CascaderOptionType[] = [];

        try {
            const domainUrls = await fetchDomainUrls({ domain, nextToken }, true).unwrap();

            if (domainUrls.urls.length === 0) {
                children = [
                    {
                        label: 'No urls found for this domain',
                        value: 'no_data',
                        disabled: true,
                    },
                ];
            } else {
                children = domainUrls.urls.map((url) => ({
                    label: url,
                    value: url,
                }));

                if (domainUrls.next_token) {
                    children.push({
                        label: <Button onClick={() => handleLoadMoreButtonClicked(domain, domainUrls.next_token!)}>Load more</Button>,
                        value: 'load_more',
                        isLeaf: false,
                    });
                }
            }
        } catch (error) { }

        return children;
    };

    const handleLoadMoreButtonClicked = async (domain: string, nextToken: string) => {
        const newChildren = await getOptionChildren(domain, nextToken);

        let updatedOptions = [...domainCascaderOptions];

        domainCascaderOptions.forEach((option, index) => {
            if (option.value === domain) {
                updatedOptions[index].children = updatedOptions[index].children?.concat(newChildren);
            }
        });

        setDomainCascaderOptions(updatedOptions);
    };

    const handleLoadDomainUrls = async (selectedOptions: CascaderOptionType[] | undefined) => {
        if (selectedOptions) {
            const targetDomainOption = selectedOptions[0];

            targetDomainOption.loading = true;
            const targetDomainChildren = await getOptionChildren(targetDomainOption.value!);
            targetDomainOption.loading = false;

            targetDomainOption.children = targetDomainChildren;

            let updatedOptions = [...domainCascaderOptions];

            domainCascaderOptions.forEach((option, index) => {
                if (option.value === targetDomainOption.value) {
                    updatedOptions[index] = targetDomainOption;
                }
            });

            setDomainCascaderOptions(updatedOptions);
        }
    };

    const handleCascaderChange = (values: string[], selectedOptions: CascaderOptionType[] | undefined) => {
        if (values.length === 1 || values[1] === 'no_data' || values[1] === 'load_more') {
            return;
        }

        onReportUrlChange(values[1]);
    };

    const displayRender = (labels: string[], selectedOptions: CascaderOptionType[] | undefined) => {
        return labels[labels.length - 1];
    };

    useEffect(() => {
        async function loadInitialOptions(domains: string[]) {
            const domainChildren = strippedReportDomain ? await getOptionChildren(strippedReportDomain) : undefined;

            setDomainCascaderOptions(
                domains.map((domain) => {
                    const strippedDomain = stripDomainWebsitePrefix(domain);

                    return {
                        value: strippedDomain,
                        label: domain,
                        isLeaf: false,
                        children: strippedDomain === strippedReportDomain ? domainChildren : undefined,
                    };
                })
            );
        }

        if (domains && domainCascaderOptions.length === 0) {
            loadInitialOptions(domains);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domains, strippedReportDomain]);

    return (
        <Cascader
            options={domainCascaderOptions}
            onChange={handleCascaderChange}
            loadData={handleLoadDomainUrls}
            value={reportUrl && strippedReportDomain ? [strippedReportDomain, reportUrl] : undefined}
            displayRender={displayRender}
            placeholder="Please select a saved report"
        />
    );
}
